import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PackageSearch, PackageCheck, Rocket, Camera, X, Truck, MapPin, FileText } from "lucide-react";
import { curSym, shipCur } from "@/lib/currency";
import AdminQuotes from "@/components/AdminQuotes";

// Sélecteur de photos : upload direct (jamais de liens/URLs)
function PhotoPicker({ photos, setPhotos, testid }) {
  const add = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - photos.length);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => setPhotos((p) => [...p, reader.result]);
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };
  return (
    <div className="col-span-2">
      <label className="flex items-center gap-1.5 text-sm font-medium cursor-pointer text-accent w-fit">
        <Camera className="h-4 w-4" /> Ajouter des photos ({photos.length}/5)
        <input type="file" accept="image/*" multiple className="hidden" onChange={add} data-testid={testid} />
      </label>
      {photos.length > 0 && (
        <div className="mt-2 flex gap-2 flex-wrap">
          {photos.map((ph, i) => (
            <div key={i} className="relative">
              <img src={ph} alt="" className="h-14 w-14 object-cover border" />
              <button type="button" onClick={() => setPhotos((p) => p.filter((_, j) => j !== i))}
                className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full p-0.5"><X className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const ROW_STATUS = {
  announced: ["Attendu", "bg-secondary text-primary"],
  in_transit: ["En transit vers le dépôt", "bg-blue-100 text-blue-700"],
  partial: ["Réception partielle", "bg-amber-100 text-amber-700"],
  received: ["Réception complète", "bg-emerald-600 text-white"],
  created: ["Attendu", "bg-secondary text-primary"],
  receiving: ["Réception partielle", "bg-amber-100 text-amber-700"],
  at_hub: ["Réception complète", "bg-emerald-600 text-white"],
  awaiting_partner: ["Réception complète", "bg-emerald-600 text-white"],
};
const LOT_STATUS = {
  to_consolidate: ["Consolidation à faire", "bg-purple-100 text-purple-700"],
  consolidating: ["Consolidation en cours", "bg-purple-600 text-white"],
  to_prepare: ["À préparer (colis séparés)", "bg-blue-100 text-blue-700"],
  consolidated: ["Consolidé — prêt pour prix final", "bg-emerald-600 text-white"],
  prepared: ["Préparé — prêt pour prix final", "bg-emerald-600 text-white"],
};
const PREFS = { group: "Regrouper mes achats", separate: "Expédier séparément", later: "Décider plus tard" };

// DEVIS (admin) : écran dédié conforme à la maquette — voir AdminQuotes.jsx.

const OpsRow = ({ r, onReceive, onAdjust, onDiff, extra }) => {
  const [lbl, cls] = ROW_STATUS[r.status] || [r.status, ""];
  return (
    <div className="border border-border bg-card p-3 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`ops-row-${r.id}`}>
      <div>
        <span className="font-semibold">{r.customer_name || "—"}</span>
        <span className="text-muted-foreground"> · {r.client_code || "—"}</span>
        {r.reference && <span className="font-mono text-xs ml-1">({r.reference})</span>}
        <div className="text-xs text-muted-foreground">
          {r.kind === "purchase" ? "Boutique" : "Provenance"} : <b>{r.source || "—"}</b>
          {r.tracking && <> · Tracking : {r.tracking}</>} · Hub : {r.hub || "—"}
          {r.dest && <> · Destination : <b data-testid={`ops-dest-${r.id}`}>{r.dest}</b></>}
          {r.lot_code && <> · {r.lot_code}</>} · {(r.created_at || "").slice(0, 10)}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold" data-testid={`ops-count-${r.id}`}>{r.received_cartons}/{r.expected} colis</span>
        <Badge className={`rounded-sm ${cls}`}>{lbl}</Badge>
        {extra}
        {onReceive && <Button size="sm" data-testid={`ops-receive-${r.id}`} className="rounded-sm bg-accent text-white h-8" onClick={() => onReceive(r)}>Réceptionner</Button>}
        {onDiff && <Button size="sm" variant="outline" data-testid={`ops-diff-${r.id}`} className="rounded-sm h-8 border-amber-500 text-amber-700" onClick={() => onDiff(r)}>Signaler une différence</Button>}
        {onAdjust && r.kind === "goods" && r.prepaid_invoice && (
          <Button size="sm" variant="outline" data-testid={`ops-adjust-${r.id}`} className="rounded-sm h-8" onClick={() => onAdjust(r)}>Prix réel</Button>
        )}
      </div>
    </div>
  );
};

// Historique des paiements admin — toutes transactions (En attente, Payé, Remboursé, Annulé…)
const TX_STATUS = {
  "Payé": "bg-emerald-600 text-white",
  "En attente": "bg-amber-100 text-amber-700",
  "Remboursé": "bg-blue-100 text-blue-700",
  "Annulé": "bg-muted text-muted-foreground",
  failed: "bg-red-100 text-red-700",
  expired: "bg-muted text-muted-foreground",
};
const PaymentsHistory = ({ hub = "all", src = "all" }) => {
  const [txns, setTxns] = useState(null);
  useEffect(() => { api.get(`/admin/payments?hub=${hub}&src=${src}`).then(({ data }) => setTxns(data.transactions || [])).catch(() => setTxns([])); }, [hub, src]);
  if (!txns) return null;
  return (
    <div className="mt-6">
      <h3 className="font-heading font-bold">Historique des paiements ({txns.length})</h3>
      <div className="mt-2 border border-border bg-card overflow-x-auto" data-testid="admin-payments-history">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Facture</th>
              <th className="px-3 py-2">Référence</th>
              <th className="px-3 py-2">Client</th>
              <th className="px-3 py-2">Montant</th>
              <th className="px-3 py-2">Moyen</th>
              <th className="px-3 py-2">Statut</th>
            </tr>
          </thead>
          <tbody>
            {txns.map((tx) => (
              <tr key={tx.session_id} data-testid={`admin-tx-${tx.session_id}`} className="border-b border-border last:border-0">
                <td className="px-3 py-2 text-muted-foreground">{(tx.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2 font-mono font-semibold">{tx.invoice_number || "—"}</td>
                <td className="px-3 py-2 font-mono">{tx.reference}</td>
                <td className="px-3 py-2">{tx.customer_name || "—"}</td>
                <td className="px-3 py-2 font-bold">{tx.amount} {curSym(tx.currency)}</td>
                <td className="px-3 py-2 capitalize">{tx.method || "stripe"}</td>
                <td className="px-3 py-2"><Badge className={`rounded-sm ${TX_STATUS[tx.status] || "bg-secondary text-primary"}`}>{tx.status}</Badge></td>
              </tr>
            ))}
            {txns.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">Aucune transaction.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Workflow simplifié : À recevoir → Au Hub (réception, consolidation, prix, paiements) →
// Prêts au départ → En transit → Arrivés / À remettre.
const HUB_SUBS = ["received", "lots", "to_price", "to_price_online", "payments"];

export const AdminOps = ({ filter, setFilter, hub = "all", src = "all" }) => {
  const known = ["quotes", "expected", ...HUB_SUBS, "ready", "transit", "arrived"];
  const sub = known.includes(filter) ? filter : "expected";
  const main = HUB_SUBS.includes(sub) ? "hub" : sub;
  const [board, setBoard] = useState(null);
  const [recv, setRecv] = useState(null);
  const [consol, setConsol] = useState(null);
  const [shipDlg, setShipDlg] = useState(null);
  const [delivDlg, setDelivDlg] = useState(null);
  const [priceDlg, setPriceDlg] = useState(null);
  const [diffDlg, setDiffDlg] = useState(null);
  const load = () => { api.get(`/admin/ops/board?hub=${hub}&src=${src}`).then(({ data }) => setBoard(data)).catch(() => {}); };
  useEffect(() => { load(); }, [hub, src]); // eslint-disable-line
  if (!board) return <p className="text-sm text-muted-foreground">Chargement…</p>;

  const lotsCount = board.lots.filter((l) => ["to_consolidate", "consolidating", "to_prepare"].includes(l.status)).length;
  const MAIN_TABS = [
    ["quotes", "Devis", FileText, (board.quotes || []).length],
    ["expected", "À recevoir", PackageSearch, board.expected.length],
    ["hub", "Au Hub", PackageCheck, board.received.length + lotsCount + (board.to_price || []).length + (board.to_price_online || []).length + (board.payments || []).length],
    ["ready", "Prêts au départ", Rocket, board.ready.length],
    ["transit", "En transit", Truck, (board.transit || []).length],
    ["arrived", "Arrivés / À remettre", MapPin, (board.arrived || []).length],
  ];
  const HUB_CHIPS = [
    ["received", "Reçus au Hub", board.received.length],
    ["lots", "À consolider / préparer", lotsCount],
    ["to_price", "Prix à fixer", (board.to_price || []).length],
    ["to_price_online", "Prix achats en ligne", (board.to_price_online || []).length],
    ["payments", "Paiements en attente", (board.payments || []).length],
  ];

  const startConsol = async (lot) => {
    try {
      await api.post(`/admin/ops/lots/${lot.id}/start`);
      toast.success(`${lot.code} — consolidation commencée`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        {MAIN_TABS.map(([v, l, Icon, n]) => (
          <button key={v} data-testid={`ops-tab-${v}`} onClick={() => setFilter(v === "hub" ? "received" : v)}
            className={`px-3 py-1.5 text-sm font-semibold border flex items-center gap-1.5 ${main === v ? "bg-primary text-white border-primary" : "border-border hover:border-accent"}`}>
            <Icon className="h-4 w-4" /> {l} ({n})
          </button>
        ))}
      </div>
      {main === "hub" && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="ops-hub-chips">
          {HUB_CHIPS.map(([v, l, n]) => (
            <button key={v} data-testid={`ops-chip-${v}`} onClick={() => setFilter(v)}
              className={`px-2.5 py-1 text-xs font-semibold border ${sub === v ? "bg-accent text-white border-accent" : "border-border hover:border-accent"}`}>
              {l} ({n})
            </button>
          ))}
        </div>
      )}

      {sub === "quotes" && (
        <AdminQuotes rows={board.quotes || []} onReceive={setRecv} onDone={load} />
      )}

      {sub === "expected" && (
        <div className="mt-4 space-y-2" data-testid="ops-expected-list">
          {board.expected.map((r) => <OpsRow key={r.id} r={r} onReceive={setRecv} />)}
          {board.expected.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center border border-dashed">Aucun colis attendu.</p>}
        </div>
      )}

      {sub === "received" && (
        <div className="mt-4 space-y-2" data-testid="ops-received-list">
          {board.received.map((r) => <OpsRow key={r.id} r={r} onDiff={(row) => setDiffDlg(row)} onAdjust={async (row) => {
            const v = window.prompt(`Prix réel constaté pour ${row.reference} (${curSym(shipCur(row))}) :`, row.final_price || "");
            if (!v) return;
            try {
              const { data } = await api.post(`/admin/ops/shipments/${row.id}/adjust`, { real_price: Number(v) });
              const rSym = curSym(data.currency || shipCur(row));
              toast.success(data.balance_due > 0 ? `Complément à payer : ${data.balance_due} ${rSym}`
                : data.refund_due > 0 ? `À rembourser : ${data.refund_due} ${rSym}` : "Prix confirmé");
              load();
            } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
          }} />)}
          {board.received.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center border border-dashed">Aucun colis reçu au dépôt.</p>}
        </div>
      )}

      {sub === "lots" && (
        <div className="mt-4 space-y-2" data-testid="ops-lots-list">
          {board.lots.map((lot) => {
            const [lbl, cls] = LOT_STATUS[lot.status] || [lot.status, ""];
            const received = lot.purchases.filter((p) => p.status === "received").length;
            return (
              <div key={lot.id} className="border border-border bg-card p-4" data-testid={`ops-lot-${lot.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-heading font-bold">{lot.code} <span className="font-normal text-sm text-muted-foreground">· {lot.customer_name} ({lot.client_code})</span></span>
                  <Badge className={`rounded-sm ${cls}`}>{lbl}</Badge>
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  {lot.purchases.length} achat(s) · {received}/{lot.purchases.length} reçu(s) · {lot.totals.cartons} carton(s) · {lot.totals.weight_kg} kg
                  {lot.totals.cbm > 0 && <> · {lot.totals.cbm} CBM</>} · Préférence : {PREFS[lot.preference] || lot.preference}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{lot.purchases.map((p) => p.shop_name).join(", ")}</div>
                {lot.consolidation && (
                  <div className="mt-1 text-xs text-emerald-700">Résultat : {lot.consolidation.cartons} colis · {lot.consolidation.weight_kg} kg{lot.consolidation.cbm > 0 && <> · {lot.consolidation.cbm} CBM</>} · par {lot.consolidation.agent_name}</div>
                )}
                <div className="mt-2 flex flex-wrap gap-2">
                  {lot.status === "to_consolidate" && (
                    <Button size="sm" data-testid={`ops-consol-start-${lot.id}`} className="rounded-sm bg-purple-600 hover:bg-purple-700 text-white h-8" onClick={() => startConsol(lot)}>Commencer la consolidation</Button>
                  )}
                  {["consolidating", "to_consolidate"].includes(lot.status) && (
                    <Button size="sm" data-testid={`ops-consol-finish-${lot.id}`} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white h-8" onClick={() => setConsol(lot)}>Terminer la consolidation</Button>
                  )}
                  {lot.status === "to_prepare" && (
                    <Button size="sm" data-testid={`ops-prepare-${lot.id}`} className="rounded-sm bg-blue-600 hover:bg-blue-700 text-white h-8" onClick={() => setConsol(lot)}>Terminer la préparation (sans regroupement)</Button>
                  )}
                </div>
              </div>
            );
          })}
          {board.lots.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center border border-dashed">Aucun lot à consolider.</p>}
        </div>
      )}

      {["to_price", "to_price_online", "payments", "ready", "transit", "arrived"].includes(sub) && (
        <div className="mt-4 space-y-2" data-testid={`ops-${sub}-list`}>
          {(board[sub] || []).map((s) => (
            s.kind === "lot"
              ? <LotPriceRow key={s.id} lot={s} onPrice={() => setPriceDlg(s)} />
              : <ShipRow key={s.id} s={s} sub={sub} onDone={load} onShip={() => setShipDlg(s)} onDeliver={() => setDelivDlg(s)} />
          ))}
          {(board[sub] || []).length === 0 && <p className="text-sm text-muted-foreground py-6 text-center border border-dashed">Aucun dossier.</p>}
        </div>
      )}

      {sub === "payments" && <PaymentsHistory hub={hub} src={src} />}

      {recv && <ReceiveDialog row={recv} onClose={() => setRecv(null)} onDone={load} />}
      {diffDlg && <DiscrepancyDialog row={diffDlg} onClose={() => setDiffDlg(null)} onDone={load} />}
      {consol && <ConsolidateDialog lot={consol} onClose={() => setConsol(null)} onDone={load} />}
      {shipDlg && <ShipDialog s={shipDlg} onClose={() => setShipDlg(null)} onDone={load} />}
      {delivDlg && <DeliverDialog s={delivDlg} onClose={() => setDelivDlg(null)} onDone={load} />}
      {priceDlg && <LotPriceDialog lot={priceDlg} onClose={() => setPriceDlg(null)} onDone={load} />}
    </div>
  );
};

const SHIP_LBL = { at_hub: "Reçu — prix à fixer", awaiting_partner: "Reçu — prix à fixer",
  paid_pending: "Paiement en attente", paid: "Payé",
  shipped: "Expédié", in_transit: "En transit", arrived: "Arrivé", available: "Disponible au retrait" };

function LotPriceRow({ lot, onPrice }) {
  const w = lot.consolidation?.weight_kg ?? lot.totals?.weight_kg ?? 0;
  const dest = lot.purchases?.[0]?.dest_country || "";
  return (
    <div className="border border-border bg-card p-3 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`ops-lotprice-${lot.id}`}>
      <span>
        <span className="font-semibold">{lot.customer_name || "—"}</span>
        <span className="text-muted-foreground"> · {lot.client_code} · {(lot.purchases || []).length} achat(s) · {w} kg</span>
        <div className="text-xs text-muted-foreground">
          {(lot.purchases || []).map((p) => p.shop_name).join(", ")}{dest && <> · → {dest}</>}
        </div>
      </span>
      <span className="flex items-center gap-2">
        <Badge className="rounded-sm bg-emerald-600 text-white">{lot.status === "consolidated" ? "Consolidé — prix à fixer" : "Préparé — prix à fixer"}</Badge>
        <Button size="sm" data-testid={`ops-lot-price-${lot.id}`} className="rounded-sm bg-accent text-white h-8" onClick={onPrice}>Fixer le prix</Button>
      </span>
    </div>
  );
}

function LotPriceDialog({ lot, onClose, onDone }) {
  const p0 = (lot.purchases || [])[0] || {};
  const [f, setF] = useState({ price: "", mode: "air", dest_country: p0.dest_country || "", dest_city: p0.dest_city || "" });
  const [quote, setQuote] = useState(null);
  const [usedCalc, setUsedCalc] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/admin/ops/lots/${lot.id}/quote`, { params: { mode: f.mode, dest_country: f.dest_country } })
      .then(({ data }) => setQuote(data)).catch(() => setQuote(null));
  }, [lot.id, f.mode, f.dest_country]); // eslint-disable-line
  const suggested = quote?.suggested?.total;
  const cur = quote?.suggested?.currency || quote?.rate?.currency || "EUR";
  const curS = curSym(cur);
  const submit = async () => {
    setBusy(true);
    try {
      // Traçabilité : prix calculé utilisé tel quel, modifié, ou saisi manuellement
      const source = suggested != null && Number(f.price) === suggested ? "calculated"
        : usedCalc ? "modified" : "manual";
      const { data } = await api.post(`/admin/ops/lots/${lot.id}/price`,
        { ...f, price: Number(f.price) || 0, price_source: source });
      toast.success(`${data.reference} — prix final ${data.final_price} ${curS} envoyé au client`);
      onClose(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="ops-price-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Fixer le prix final</DialogTitle></DialogHeader>
        {/* Détails du dossier */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm border border-border bg-secondary/40 p-3" data-testid="price-dossier-info">
          <span className="text-muted-foreground">Client</span><span className="font-semibold">{quote?.customer_name || lot.customer_name}</span>
          <span className="text-muted-foreground">Référence C2G</span><span className="font-mono font-semibold">{quote?.client_code || lot.client_code}</span>
          <span className="text-muted-foreground">Hub de départ</span><span>{quote?.hub || "—"}</span>
          <span className="text-muted-foreground">Nombre de colis</span><span>{quote?.cartons ?? lot.totals?.cartons ?? "—"}</span>
          <span className="text-muted-foreground">Poids final</span><span data-testid="price-weight">{quote?.weight_kg ?? 0} kg</span>
          {(quote?.cbm || 0) > 0 && <><span className="text-muted-foreground">CBM</span><span>{quote.cbm}</span></>}
          <span className="text-muted-foreground">Contenu</span><span className="truncate">{(quote?.shops || []).join(", ")}</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Pays de destination</Label><Input data-testid="lot-price-dest" className="rounded-sm" value={f.dest_country} onChange={(e) => setF({ ...f, dest_country: e.target.value })} /></div>
          <div><Label>Ville (optionnel)</Label><Input className="rounded-sm" value={f.dest_city} onChange={(e) => setF({ ...f, dest_city: e.target.value })} /></div>
          <div className="col-span-2"><Label>Mode</Label>
            <div className="flex gap-1.5 mt-1">
              {[["air", "Aérien / Express"], ["sea", "Maritime"]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`lot-price-mode-${v}`} onClick={() => setF({ ...f, mode: v })}
                  className={`px-3 py-1.5 text-sm font-semibold border ${f.mode === v ? "bg-primary text-white border-primary" : "border-border"}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>
        {/* Prix calculé par le moteur de tarifs admin (jamais imposé — modifiable librement) */}
        {quote?.rate ? (
          <div className="border border-accent/40 bg-accent/5 p-3 text-sm" data-testid="price-suggestion">
            <div className="text-xs uppercase font-bold text-accent">Règle tarifaire applicable</div>
            <div className="mt-0.5 text-muted-foreground">
              {quote.rate.label || quote.rate.type_label}
              {quote.rate.min_charge > 0 && <> · min {quote.rate.min_charge} {curSym}</>}
            </div>
            {suggested != null && (
              <div className="mt-2 flex items-center justify-between gap-2">
                <span>Prix client calculé : <b className="text-accent text-lg" data-testid="suggested-price">{suggested} {curSym}</b>
                  {quote.rate.cost != null && (
                    <span className="block text-xs text-muted-foreground" data-testid="internal-cost">
                      Coût Colis2Go : {quote.rate.cost} {curSym} · Marge : {quote.rate.margin} {curSym} (interne — jamais visible client)
                    </span>
                  )}
                </span>
                <Button size="sm" variant="outline" data-testid="use-suggested-price" className="rounded-sm h-8"
                  onClick={() => { setF({ ...f, price: String(suggested) }); setUsedCalc(true); }}>Utiliser le prix calculé</Button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground" data-testid="no-rate-note">Pas de règle tarifaire pour cette destination — saisissez un prix manuel (devis). Gérez vos règles dans l'onglet Tarifs.</p>
        )}
        <div><Label>Prix final ({curSym}) — modifiable librement</Label>
          <Input data-testid="lot-price-input" type="number" className="rounded-sm" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} /></div>
        <DialogFooter><Button data-testid="lot-price-confirm" disabled={busy} className="rounded-sm bg-accent text-white" onClick={submit}>ENVOYER LE PRIX AU CLIENT</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShipRow({ s, sub, onDone, onShip, onDeliver }) {
  const setStatus = async (status) => {
    try {
      await api.post(`/admin/ops/shipments/${s.id}/status`, { status });
      toast.success(`${s.reference} → ${SHIP_LBL[status] || status}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const confirmPay = async () => {
    try {
      await api.put(`/admin/shipments/${s.id}/payment`, { status: "confirmed", link: "", note: "", method: "" });
      toast.success(`${s.reference} — paiement confirmé`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <div className="border border-border bg-card p-3 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`ops-ship-${s.id}`}>
      <span>
        <span className="font-mono font-semibold">{s.reference}</span> · {s.customer_name}
        <span className="text-muted-foreground"> · {s.origin_city || s.hub_name || "Colis2Go"} → {s.dest_city || s.dest_country}</span>
        {s.final_price != null && <span className="font-semibold"> · {s.final_price} {s.ss_rate?.currency || "EUR"}</span>}
      </span>
      <span className="flex items-center gap-2 flex-wrap">
        <Badge className="rounded-sm bg-secondary text-primary">{SHIP_LBL[s.status] || s.status}</Badge>
        {sub === "to_price" && <Button size="sm" data-testid={`ops-price-${s.id}`} className="rounded-sm bg-accent text-white h-8" onClick={() => { window.location.href = `/shipments/${s.id}`; }}>Fixer le prix</Button>}
        {sub === "payments" && <Button size="sm" data-testid={`ops-pay-${s.id}`} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white h-8" onClick={confirmPay}>Confirmer le paiement</Button>}
        {sub === "ready" && <Button size="sm" data-testid={`ops-shipit-${s.id}`} className="rounded-sm bg-primary text-white h-8" onClick={onShip}>Marquer comme expédié</Button>}
        {sub === "transit" && s.status === "shipped" && <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => setStatus("in_transit")}>Passer en transit</Button>}
        {sub === "transit" && <Button size="sm" data-testid={`ops-arrived-${s.id}`} className="rounded-sm bg-primary text-white h-8" onClick={() => setStatus("arrived")}>Marquer comme arrivé</Button>}
        {sub === "arrived" && s.status === "arrived" && <Button size="sm" data-testid={`ops-avail-${s.id}`} className="rounded-sm bg-primary text-white h-8" onClick={() => setStatus("available")}>Disponible au retrait</Button>}
        {sub === "arrived" && s.status === "available" && <Button size="sm" data-testid={`ops-deliver-${s.id}`} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white h-8" onClick={onDeliver}>Retiré / Livré</Button>}
      </span>
    </div>
  );
}

function ShipDialog({ s, onClose, onDone }) {
  const [f, setF] = useState({ departed_at: "", awb: "", transport: "", eta: "", delivery_notes: "" });
  const go = async () => {
    try {
      await api.post(`/admin/ops/shipments/${s.id}/status`, { status: "shipped", ...f });
      toast.success(`${s.reference} — expédié`); onClose(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="ops-ship-dialog">
        <DialogHeader><DialogTitle>Marquer comme expédié — {s.reference}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Date départ</Label><Input type="date" className="rounded-sm" value={f.departed_at} onChange={(e) => setF({ ...f, departed_at: e.target.value })} /></div>
          <div><Label>AWB / BL</Label><Input className="rounded-sm" value={f.awb} onChange={(e) => setF({ ...f, awb: e.target.value })} /></div>
          <div><Label>Transport</Label><Input className="rounded-sm" value={f.transport} onChange={(e) => setF({ ...f, transport: e.target.value })} /></div>
          <div><Label>ETA</Label><Input type="date" className="rounded-sm" value={f.eta} onChange={(e) => setF({ ...f, eta: e.target.value })} /></div>
          <div className="col-span-2"><Label>Notes</Label><Input className="rounded-sm" value={f.delivery_notes} onChange={(e) => setF({ ...f, delivery_notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button data-testid="ops-ship-confirm" className="rounded-sm bg-primary text-white" onClick={go}>Marquer comme expédié</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SignaturePad({ onChange }) {
  const ref = React.useRef(null);
  const drawing = React.useRef(false);
  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (ref.current.width / r.width), y: (t.clientY - r.top) * (ref.current.height / r.height) };
  };
  const start = (e) => { drawing.current = true; const c = ref.current.getContext("2d"); const p = pos(e); c.beginPath(); c.moveTo(p.x, p.y); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = ref.current.getContext("2d"); const p = pos(e);
    c.lineWidth = 2; c.lineCap = "round"; c.strokeStyle = "#1a1a1a";
    c.lineTo(p.x, p.y); c.stroke();
  };
  const end = () => { if (!drawing.current) return; drawing.current = false; onChange(ref.current.toDataURL("image/png")); };
  const clear = () => { const c = ref.current.getContext("2d"); c.clearRect(0, 0, ref.current.width, ref.current.height); onChange(""); };
  return (
    <div className="col-span-2">
      <div className="flex items-center justify-between">
        <Label>Signature du destinataire *</Label>
        <Button type="button" size="sm" variant="ghost" className="h-6 text-xs" data-testid="signature-clear" onClick={clear}>Effacer</Button>
      </div>
      <canvas ref={ref} width={420} height={140} data-testid="signature-pad"
        className="mt-1 w-full border border-dashed border-border bg-white touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
    </div>
  );
}

function DeliverDialog({ s, onClose, onDone }) {
  const [f, setF] = useState({ picked_by: "", phone: "", pickup_code: "", code_override: false, override_reason: "" });
  const [signature, setSignature] = useState("");
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/admin/ops/shipments/${s.id}/pickup-status`).then(({ data }) => setInfo(data)).catch(() => {});
  }, [s.id]);
  const go = async () => {
    if (!signature) { toast.error("Signature du destinataire requise"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/ops/shipments/${s.id}/status`, { status: "delivered", ...f, signature });
      toast.success(`${s.reference} — retiré / livré. Reçu de remise archivé.`);
      try {
        const { data } = await api.get(`/admin/ops/shipments/${s.id}/delivery-receipt`, { responseType: "blob" });
        window.open(URL.createObjectURL(data), "_blank");
      } catch { /* reçu téléchargeable plus tard */ }
      onClose(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="ops-deliver-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Retiré / Livré — {s.reference}</DialogTitle></DialogHeader>
        {info?.pickup_person && (
          <p className="text-sm border border-border bg-secondary/40 p-2" data-testid="deliver-auth-person">
            Personne autorisée par le client : <b>{info.pickup_person.name}</b>
            {info.pickup_person.phone && <> · {info.pickup_person.phone}</>}
            {info.pickup_person.id_number && <> · Pièce : {info.pickup_person.id_number}</>}
          </p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Personne ayant retiré</Label><Input data-testid="deliver-name" className="rounded-sm" value={f.picked_by} onChange={(e) => setF({ ...f, picked_by: e.target.value })} /></div>
          <div><Label>Téléphone</Label><Input className="rounded-sm" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
          <div className="col-span-2"><Label>Code de retrait (reçu par email par le client)</Label>
            <Input data-testid="deliver-code" className="rounded-sm font-mono tracking-widest" maxLength={6} placeholder="000000"
              value={f.pickup_code} onChange={(e) => setF({ ...f, pickup_code: e.target.value })} /></div>
          <label className="col-span-2 flex items-center gap-2 text-sm font-semibold text-amber-700">
            <Checkbox data-testid="deliver-override" checked={f.code_override} onCheckedChange={(v) => setF({ ...f, code_override: !!v })} />
            Dérogation — remettre sans code (motif obligatoire)
          </label>
          {f.code_override && (
            <div className="col-span-2"><Label>Motif de la dérogation *</Label>
              <Input data-testid="deliver-override-reason" className="rounded-sm" placeholder="Ex. : client identifié par pièce d'identité, email inaccessible…"
                value={f.override_reason} onChange={(e) => setF({ ...f, override_reason: e.target.value })} /></div>
          )}
          <SignaturePad onChange={setSignature} />
        </div>
        <DialogFooter><Button data-testid="deliver-confirm" disabled={busy} className="rounded-sm bg-emerald-600 text-white" onClick={go}>Confirmer la remise</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// SIGNALER UNE DIFFÉRENCE : déclaré vs réellement reçu (quantité, poids, dimensions, note, photos)
function DiscrepancyDialog({ row, onClose, onDone }) {
  const [f, setF] = useState({ declared: `${row.expected || "?"} colis — ${row.source || row.reference || ""}`,
    received: "", qty_real: "", weight_real: "", dims_real: "", note: "" });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  // Communication au client : WhatsApp / Email / photo, message modifiable
  const firstName = (row.customer_name || "").split(" ")[0] || "client";
  const [comm, setComm] = useState({ whatsapp: true, email: true, photo: true });
  // SOURCE PRINCIPALE : message unique construit depuis les champs du formulaire,
  // envoyé À L'IDENTIQUE par WhatsApp et Email. Mis à jour en direct tant que
  // l'admin ne l'a pas modifié manuellement.
  const buildMsg = (ff) => {
    let m = `Bonjour ${firstName},\nLors de la réception de votre colis ${row.reference || row.source || ""}, notre équipe a constaté une différence avec votre déclaration.`;
    m += `\n\nDéclaré par le client : ${ff.declared || "—"}`;
    m += `\nRéellement reçu : ${ff.received || "—"}`;
    if (Number(ff.qty_real) > 0) m += `\nQuantité réelle : ${ff.qty_real}`;
    if (Number(ff.weight_real) > 0) m += `\nPoids réel : ${ff.weight_real} kg`;
    if (ff.dims_real) m += `\nDimensions réelles : ${ff.dims_real}`;
    if (ff.note) m += `\nNote : ${ff.note}`;
    return m;
  };
  const [msgEdited, setMsgEdited] = useState(false);
  const [commMsg, setCommMsg] = useState(() => buildMsg(f));
  const upd = (nf) => { setF(nf); if (!msgEdited) setCommMsg(buildMsg(nf)); };
  const submit = async (force = false) => {
    if (!f.received.trim()) { toast.error("Précisez ce qui a réellement été reçu"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ops/discrepancy", {
        kind: row.kind === "purchase" ? "purchase" : "goods", id: row.id, ...f,
        qty_real: Number(f.qty_real) || 0, weight_real: Number(f.weight_real) || 0, photos,
        notify_email: comm.email, notify_whatsapp: comm.whatsapp,
        attach_photo: comm.photo && photos.length > 0, custom_message: commMsg, force });
      if (data.whatsapp_link) window.open(data.whatsapp_link, "_blank");
      const via = [comm.email && "email", comm.whatsapp && "WhatsApp"].filter(Boolean).join(" + ");
      toast.success(data.price_update
        ? `Différence signalée — prix mis à jour : ${data.price_update.new} ${curSym(data.price_update.currency)}.${via ? ` Client notifié par ${via}.` : ""}`
        : `Différence signalée${via ? ` — client notifié par ${via}` : ""}.`);
      onClose(); onDone();
    } catch (e) {
      if (e.response?.status === 409) {
        setBusy(false);
        if (window.confirm("Cette différence identique a déjà été signalée pour ce dossier. Voulez-vous vraiment la signaler à nouveau ?")) return submit(true);
        return;
      }
      toast.error(e.response?.data?.detail || "Erreur");
    }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="ops-diff-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Signaler une différence — {row.customer_name || ""}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">{row.client_code} · {row.source || row.reference} · Le client sera notifié selon les canaux cochés ci-dessous. Si un prix était déjà fixé (non payé), il sera recalculé.</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Déclaré par le client</Label>
            <Input data-testid="diff-declared" className="rounded-sm" value={f.declared} onChange={(e) => upd({ ...f, declared: e.target.value })} /></div>
          <div className="col-span-2"><Label>Réellement reçu *</Label>
            <Input data-testid="diff-received" className="rounded-sm" placeholder="Ex. : 3 cartons au lieu de 5, contenu différent…" value={f.received} onChange={(e) => upd({ ...f, received: e.target.value })} /></div>
          <div><Label>Quantité réelle</Label>
            <Input data-testid="diff-qty" type="number" className="rounded-sm" value={f.qty_real} onChange={(e) => upd({ ...f, qty_real: e.target.value })} /></div>
          <div><Label>Poids réel (kg)</Label>
            <Input data-testid="diff-weight" type="number" className="rounded-sm" value={f.weight_real} onChange={(e) => upd({ ...f, weight_real: e.target.value })} /></div>
          <div className="col-span-2"><Label>Dimensions réelles</Label>
            <Input data-testid="diff-dims" className="rounded-sm" placeholder="Ex. : 60 × 40 × 40 cm" value={f.dims_real} onChange={(e) => upd({ ...f, dims_real: e.target.value })} /></div>
          <PhotoPicker photos={photos} setPhotos={setPhotos} testid="diff-photos" />
          <div className="col-span-2"><Label>Note</Label>
            <Input data-testid="diff-note" className="rounded-sm" value={f.note} onChange={(e) => upd({ ...f, note: e.target.value })} /></div>
          <div className="col-span-2 border-t pt-3">
            <div className="text-sm font-heading font-bold">Informer le client</div>
            <div className="mt-1.5 flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox data-testid="diff-comm-whatsapp" checked={comm.whatsapp} onCheckedChange={(v) => setComm({ ...comm, whatsapp: !!v })} /> WhatsApp
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox data-testid="diff-comm-email" checked={comm.email} onCheckedChange={(v) => setComm({ ...comm, email: !!v })} /> Email
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox data-testid="diff-comm-photo" checked={comm.photo} onCheckedChange={(v) => setComm({ ...comm, photo: !!v })} /> Joindre la photo
              </label>
            </div>
            <textarea data-testid="diff-comm-message" className="mt-2 w-full min-h-[110px] border border-border bg-card p-2 text-sm"
              value={commMsg} onChange={(e) => { setMsgEdited(true); setCommMsg(e.target.value); }} />
            <p className="text-xs text-muted-foreground mt-1">Ce message (modifiable) sera envoyé tel quel par WhatsApp et Email. Il se met à jour automatiquement avec les champs ci-dessus tant que vous ne l'avez pas modifié.</p>
          </div>
        </div>
        <DialogFooter><Button data-testid="diff-confirm" disabled={busy} className="rounded-sm bg-accent text-white" onClick={() => submit()}>SIGNALER ET NOTIFIER LE CLIENT</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReceiveDialog({ row, onClose, onDone }) {
  const [f, setF] = useState({ cartons: 1, weight_kg: "", length_cm: "", width_cm: "", height_cm: "", tracking_number: row.tracking || "", condition: "good", damaged: false, notes: "" });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  // COMPARAISON AUTO : déclaré par le client VS réellement reçu (les 2 parcours)
  const expected = Number(row.expected) || 0;
  const already = Number(row.received_cartons) || 0;
  const entering = Number(f.cartons) || 0;
  const totalAfter = already + entering;
  const over = expected > 0 && totalAfter > expected;
  const under = expected > 0 && totalAfter < expected;
  const [diffTouched, setDiffTouched] = useState(false);
  const [diffOn, setDiffOn] = useState(false);
  const [diffDesc, setDiffDesc] = useState("");
  useEffect(() => {
    if (!diffTouched) {
      setDiffOn(over);
      // Bascule aussi le message sur le bon modèle (réception ↔ différence)
      if (!msgEdited) setCommMsg(buildMsg(f, over, diffDesc));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [over, diffTouched]);
  // Communication au client : WhatsApp / Email / photo, message unique modifiable
  const firstName = (row.customer_name || "").split(" ")[0] || "client";
  const [comm, setComm] = useState({ whatsapp: true, email: true, photo: true });
  const dimsStr = Number(f.length_cm) > 0 ? `${f.length_cm} × ${f.width_cm} × ${f.height_cm} cm` : "";
  const buildMsg = (ff, dOn, dDesc) => {
    const ref = row.reference || row.source || "";
    if (!dOn) return `Bonjour ${firstName},\nVotre colis ${ref} a bien été reçu au Hub Colis2Go.`;
    let m = `Bonjour ${firstName},\nLors de la réception de votre colis ${ref}, notre équipe a constaté une différence avec votre déclaration.`;
    m += `\n\nDéclaré par le client : ${expected || "?"} colis — ${ref}`;
    m += `\nRéellement reçu : ${dDesc || `${totalAfter} colis au total`}`;
    if (Number(ff.weight_kg) > 0) m += `\nPoids réel : ${ff.weight_kg} kg`;
    const d = Number(ff.length_cm) > 0 ? `${ff.length_cm} × ${ff.width_cm} × ${ff.height_cm} cm` : "";
    if (d) m += `\nDimensions réelles : ${d}`;
    if (ff.notes) m += `\nNote : ${ff.notes}`;
    return m;
  };
  const [msgEdited, setMsgEdited] = useState(false);
  const [commMsg, setCommMsg] = useState(() => buildMsg(f, false, ""));
  const upd = (nf) => { setF(nf); if (!msgEdited) setCommMsg(buildMsg(nf, diffOn, diffDesc)); };
  const updDiff = (on, desc) => {
    setDiffOn(on); setDiffDesc(desc);
    if (!msgEdited) setCommMsg(buildMsg(f, on, desc));
  };
  const submit = async () => {
    setBusy(true);
    const base = { cartons: Number(f.cartons) || 1, weight_kg: Number(f.weight_kg) || 0,
      length_cm: Number(f.length_cm) || 0, width_cm: Number(f.width_cm) || 0, height_cm: Number(f.height_cm) || 0,
      photos,
      condition: f.damaged ? "damaged" : f.condition, notes: f.notes };
    try {
      // 1) RÉCEPTION (fonction existante) — enregistre données réelles + statut + historique
      let partial, priceUpd = null;
      if (row.kind === "purchase") {
        const { data } = await api.post("/admin/ops/receive", { ...base, purchase_id: row.id, damaged: f.damaged, tracking_number: f.tracking_number });
        partial = data.purchase_status === "partial";
      } else {
        const { data } = await api.post("/admin/hubs/receive", { ...base, reference: row.reference, supplier_name: row.source || "Marchandise client" });
        partial = data.reception === "partial";
        priceUpd = data.price_update || null; // recalcul auto (prix déjà fixé)
      }
      // 2) DIFFÉRENCE dans la même fenêtre (fonction existante) — une seule validation
      let diffFailed = false;
      if (diffOn) {
        try {
          const { data: d2 } = await api.post("/admin/ops/discrepancy", {
            kind: row.kind === "purchase" ? "purchase" : "goods", id: row.id,
            declared: `${expected || "?"} colis — ${row.source || row.reference || ""}`,
            received: diffDesc || `${totalAfter} colis au total au lieu de ${expected}`,
            qty_real: totalAfter, weight_real: Number(f.weight_kg) || 0,
            dims_real: dimsStr, note: f.notes, photos,
            notify_email: comm.email, notify_whatsapp: comm.whatsapp,
            attach_photo: comm.photo && photos.length > 0, custom_message: commMsg });
          if (d2.whatsapp_link) window.open(d2.whatsapp_link, "_blank");
          priceUpd = d2.price_update || priceUpd;
        } catch { diffFailed = true; }
      } else {
        // Pas de différence → communication de réception classique (fonction existante)
        const channels = ["whatsapp", "email"].filter((k) => comm[k]);
        if (channels.length && commMsg.trim()) {
          try {
            const { data: c } = await api.post("/admin/comms/send", {
              shipment_id: row.kind === "goods" ? row.id : undefined,
              purchase_id: row.kind === "purchase" ? row.id : undefined,
              reference: row.reference || row.source || "", channels, message: commMsg,
              subject: `Colis reçu au Hub — ${row.reference || row.source || ""}`,
              attach_photo: comm.photo && photos.length > 0, photos,
            });
            if (c.whatsapp_link) window.open(c.whatsapp_link, "_blank");
          } catch { /* non bloquant */ }
        }
      }
      let m = partial ? "Réception partielle enregistrée" : "Réception complète — tous les colis sont arrivés";
      if (diffOn && !diffFailed) m += " · différence enregistrée";
      if (priceUpd) m += priceUpd.kind === "adjustment"
        ? ` · complément à payer : ${priceUpd.diff} ${curSym(priceUpd.currency)}`
        : priceUpd.kind === "credit"
          ? ` · avoir à rembourser : ${Math.abs(priceUpd.diff)} ${curSym(priceUpd.currency)}`
          : ` · prix mis à jour : ${priceUpd.new} ${curSym(priceUpd.currency)}`;
      toast.success(m);
      if (diffFailed) toast.error("Réception validée, mais la différence n'a pas pu être enregistrée — utilisez « Signaler une différence » dans Au Hub.");
      onClose(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="ops-receive-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Réceptionner — {row.source || row.reference}</DialogTitle>
          <DialogDescription>{row.customer_name} ({row.client_code}) · attendu : {row.expected} colis · déjà reçu : {row.received_cartons}</DialogDescription></DialogHeader>
        {/* COMPARAISON AUTOMATIQUE DÉCLARÉ vs REÇU */}
        {expected > 0 && (
          <div data-testid="recv-compare" className={`flex items-center justify-between border px-3 py-2 text-sm ${over ? "border-red-300 bg-red-50" : under ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"}`}>
            <span>Déclaré : <b>{expected}</b> colis · Après cette réception : <b>{totalAfter}</b></span>
            <Badge className={`rounded-sm ${over ? "bg-red-600" : under ? "bg-amber-500" : "bg-emerald-600"} text-white`}>
              {over ? "DIFFÉRENCE" : under ? "PARTIEL" : "CONFORME"}</Badge>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Nombre reçu</Label><Input data-testid="recv-cartons" type="number" className="rounded-sm" value={f.cartons} onChange={(e) => upd({ ...f, cartons: e.target.value })} /></div>
          <div><Label>Poids (kg)</Label><Input data-testid="recv-weight" type="number" className="rounded-sm" value={f.weight_kg} onChange={(e) => upd({ ...f, weight_kg: e.target.value })} /></div>
          <div><Label>Longueur (cm)</Label><Input type="number" className="rounded-sm" value={f.length_cm} onChange={(e) => upd({ ...f, length_cm: e.target.value })} /></div>
          <div><Label>Largeur (cm)</Label><Input type="number" className="rounded-sm" value={f.width_cm} onChange={(e) => upd({ ...f, width_cm: e.target.value })} /></div>
          <div><Label>Hauteur (cm)</Label><Input type="number" className="rounded-sm" value={f.height_cm} onChange={(e) => upd({ ...f, height_cm: e.target.value })} /></div>
          <div><Label>Tracking</Label><Input data-testid="recv-tracking" className="rounded-sm" value={f.tracking_number} onChange={(e) => setF({ ...f, tracking_number: e.target.value })} /></div>
          <PhotoPicker photos={photos} setPhotos={setPhotos} testid="recv-photos" />
          <div className="col-span-2"><Label>Notes / état</Label><Input data-testid="recv-notes" className="rounded-sm" value={f.notes} onChange={(e) => upd({ ...f, notes: e.target.value })} /></div>
          <label className="col-span-2 flex items-center gap-2 text-sm font-semibold text-red-700">
            <Checkbox data-testid="recv-damaged" checked={f.damaged} onCheckedChange={(v) => setF({ ...f, damaged: !!v })} /> Colis endommagé
          </label>
          {/* DIFFÉRENCE traitée DANS la même fenêtre — une seule validation */}
          <div className="col-span-2 border-t pt-3">
            <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
              <Checkbox data-testid="recv-diff-toggle" checked={diffOn}
                onCheckedChange={(v) => { setDiffTouched(true); updDiff(!!v, diffDesc); }} />
              Différence constatée (déclaré ≠ réellement reçu)
            </label>
            {under && !diffOn && (
              <p className="text-xs text-amber-700 mt-1">Réception partielle : si le reste n'arrivera jamais, cochez la case pour signaler la différence maintenant.</p>
            )}
            {diffOn && (
              <div className="mt-2 space-y-2">
                <div className="text-xs text-muted-foreground">Déclaré par le client : <b>{expected || "?"} colis — {row.source || row.reference}</b> · Quantité réelle : <b>{totalAfter}</b>{Number(f.weight_kg) > 0 ? <> · Poids réel : <b>{f.weight_kg} kg</b></> : null}{dimsStr ? <> · Dimensions : <b>{dimsStr}</b></> : null}</div>
                <div><Label>Réellement reçu (description)</Label>
                  <Input data-testid="recv-diff-received" className="rounded-sm" placeholder={`Ex. : ${totalAfter} colis au total au lieu de ${expected}, contenu différent…`}
                    value={diffDesc} onChange={(e) => { setDiffTouched(true); updDiff(true, e.target.value); }} /></div>
              </div>
            )}
          </div>
          <div className="col-span-2 border-t pt-3">
            <div className="text-sm font-heading font-bold">Informer le client</div>
            <div className="mt-1.5 flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox data-testid="recv-comm-whatsapp" checked={comm.whatsapp} onCheckedChange={(v) => setComm({ ...comm, whatsapp: !!v })} /> WhatsApp
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox data-testid="recv-comm-email" checked={comm.email} onCheckedChange={(v) => setComm({ ...comm, email: !!v })} /> Email
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox data-testid="recv-comm-photo" checked={comm.photo} onCheckedChange={(v) => setComm({ ...comm, photo: !!v })} /> Joindre la photo
              </label>
            </div>
            <textarea data-testid="recv-comm-message" className="mt-2 w-full min-h-[90px] border border-border bg-card p-2 text-sm"
              value={commMsg} onChange={(e) => { setMsgEdited(true); setCommMsg(e.target.value); }} />
            <p className="text-xs text-muted-foreground mt-1">Ce message (modifiable) est envoyé par les canaux cochés. Il s'adapte automatiquement (réception ou différence) tant que vous ne l'avez pas modifié.</p>
          </div>
        </div>
        <DialogFooter><Button data-testid="recv-confirm" disabled={busy} className="rounded-sm bg-accent text-white" onClick={submit}>{diffOn ? "Valider la réception + différence" : "Valider la réception"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConsolidateDialog({ lot, onClose, onDone }) {
  const [f, setF] = useState({ cartons: 1, weight_kg: "", length_cm: "", width_cm: "", height_cm: "", cbm: "", notes: "" });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/ops/lots/${lot.id}/consolidate`, {
        cartons: Number(f.cartons) || 1, weight_kg: Number(f.weight_kg) || 0,
        length_cm: Number(f.length_cm) || 0, width_cm: Number(f.width_cm) || 0, height_cm: Number(f.height_cm) || 0,
        cbm: Number(f.cbm) || 0, photos, notes: f.notes });
      toast.success(data.status === "consolidated" ? `${lot.code} consolidé — prêt pour prix final` : `${lot.code} préparé — prêt pour prix final`);
      onClose(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="ops-consolidate-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{lot.status === "to_prepare" ? "Terminer la préparation" : "Terminer la consolidation"} — {lot.code}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">{lot.customer_name} · {lot.purchases.length} achat(s) · avant regroupement : {lot.totals.cartons} carton(s), {lot.totals.weight_kg} kg</p>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Nombre final de colis</Label><Input data-testid="consol-cartons" type="number" className="rounded-sm" value={f.cartons} onChange={(e) => setF({ ...f, cartons: e.target.value })} /></div>
          <div><Label>Poids final (kg)</Label><Input data-testid="consol-weight" type="number" className="rounded-sm" value={f.weight_kg} onChange={(e) => setF({ ...f, weight_kg: e.target.value })} /></div>
          <div><Label>Longueur (cm)</Label><Input type="number" className="rounded-sm" value={f.length_cm} onChange={(e) => setF({ ...f, length_cm: e.target.value })} /></div>
          <div><Label>Largeur (cm)</Label><Input type="number" className="rounded-sm" value={f.width_cm} onChange={(e) => setF({ ...f, width_cm: e.target.value })} /></div>
          <div><Label>Hauteur (cm)</Label><Input type="number" className="rounded-sm" value={f.height_cm} onChange={(e) => setF({ ...f, height_cm: e.target.value })} /></div>
          <div><Label>CBM (si maritime)</Label><Input data-testid="consol-cbm" type="number" className="rounded-sm" value={f.cbm} onChange={(e) => setF({ ...f, cbm: e.target.value })} /></div>
          <PhotoPicker photos={photos} setPhotos={setPhotos} testid="consol-photos" />
          <div className="col-span-2"><Label>Notes</Label><Input data-testid="consol-notes" className="rounded-sm" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button data-testid="consol-confirm" disabled={busy} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" onClick={submit}>{lot.status === "to_prepare" ? "Terminer la préparation" : "Terminer la consolidation"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
